import {
  Box,
  Flex,
  Image,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import type {
  CellContext,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import { formatPhoneNumber } from '@revelio/core';
import { TrialType } from '@revelio/data-access';
import {
  FormatType,
  getFormatter,
  TableChart,
  useChartTheme,
} from '@revelio/replots';

import { CustomiseColumnsButton } from '../../../../../shared/components/customise-columns-button';
import { DefaultIcon } from '../../../../../shared/ui/icons';
import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { RESTRICTED_TRIAL_COLUMNS } from '../../../constants/trialColumns';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useTrialUserBlurLogic } from '../../../hooks/useTrialUserBlurLogic';
import BlurredCell from '../../BlurredCell';
import { DataType } from '../../DataProvider/types';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import ProfileUrlCell from '../../ProfileUrlCell';
import {
  type TableRow,
  useTableSelection,
} from '../../TableSelectionProvider/TableSelectionProvider';
import { useChart } from '../ChartProvider';
import { useChartMenuContext } from '../chart-menu/ChartMenuContext';
import { getFormat } from '../utils/getFormat';
import { ColumnSelectModal } from './column-select-modal';
import { RowPopout } from './RowPopoutSelector';
import styles from './Table.module.css';
import { useTableControls } from './TableControlsContext';
import {
  type ResolvedColumnProcessor,
  useTableColumnSync,
} from './useTableColumnSync';
import type { ColumnMetaMap } from './utils/convertSeriesToTableData';
import { getTalentDiscoveryFormat } from './utils/getTalentDiscoveryFormat';
import {
  flightRiskSortingFn,
  getPrimaryColumnHeader,
  getTableColumnHeader,
  numericSortingFn,
  prestigeSortingFn,
  remoteSuitabilitySortingFn,
} from './utils/table-helpers';
import { migrateColumnNames } from './utils/talent-discovery-column-mappings';

const monthAbbreviations = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatDateToMMMYYYY = (
  dateString: string | number | null | undefined
): string => {
  if (!dateString || dateString === '') {
    return String(dateString ?? '');
  }

  const dateStr = String(dateString);
  const [year, month] = dateStr.split('-');

  if (!year || !month) {
    return dateStr;
  }

  const monthIndex = Number(month);
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  if (isNaN(monthIndex) || monthIndex < 1 || monthIndex > 12) {
    return dateStr;
  }

  return `${monthAbbreviations[monthIndex - 1]} ${year}`;
};

const parseUserKey = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const getRowUserKey = (row: Record<string, unknown>): string | undefined =>
  parseUserKey(row.original_user_id) ?? parseUserKey(row.user_id);

type TableProps = {
  data: TableRow[];
  columnMetaMap?: ColumnMetaMap;
  onSelectRow?: (data: object) => void;
  onSortedDataChange?: (sortedRows: TableRow[]) => void;
  onSortingStateNotify?: (sorting: SortingState) => void;
  enableRowSelection?: boolean;
  filterColumn?: (key: string) => boolean;
  numericSortKeys?: Set<string>;
  defaultColumns?: string[];
  getRowDetail?: (row: TableRow) => ReactNode;
  columnStorageKey?: string;
  processResolvedColumns?: ResolvedColumnProcessor;
};

export const Table = ({
  data,
  columnMetaMap,
  onSelectRow,
  onSortedDataChange,
  onSortingStateNotify,
  enableRowSelection = true,
  filterColumn,
  numericSortKeys,
  defaultColumns,
  getRowDetail,
  columnStorageKey = 'columns_custom_dash',
  processResolvedColumns,
}: TableProps) => {
  const {
    searchTerm,
    availableColumns,
    setAvailableColumns,
    selectedColumns,
    setSelectedColumns,
    setIsEmpty,
  } = useTableControls();

  const { item, variant } = useChart();
  const { isNewTheme } = useChartTheme();
  const showZebraStripe = item?.plot?.showZebraStripe;
  const isReport = variant === 'report';
  const { primaryFilter } = useTabContentConfig();
  const primaryDimension = primaryFilter?.id;
  const { setCustomData } = useChartMenuContext();
  const { isTrialUser, showBlurToast } = useTrialUserBlurLogic();
  const { code: currencyCode } = useCurrencyPreference();
  const {
    isOpen: isColumnModalOpen,
    onOpen: onColumnModalOpen,
    onClose: onColumnModalClose,
  } = useDisclosure();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { trialType } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const allowUnblur = useMemo(() => {
    return isTrialUser && trialType === TrialType.NO_DATA_LAG_WITH_TD_REVEAL;
  }, [isTrialUser, trialType]);

  const boundaryRef = useRef<HTMLDivElement>(null);

  // Update isEmpty state when filtered data changes
  useEffect(() => {
    setIsEmpty(data?.length === 0);
  }, [data.length, setIsEmpty]);

  const effectiveFilters = useEffectiveFilters();
  const hasVisibleFilters = effectiveFilters.some((filter) => filter.isVisible);

  const allKeys = Array.from(
    new Set(data.flatMap((row: Record<string, unknown>) => Object.keys(row)))
  ).filter((key) => (filterColumn ? filterColumn(key) : true));

  // Deduplicate available columns by their headers
  const deduplicatedAvailableColumns = useMemo(() => {
    const filteredKeys = allKeys.filter((k) => !k.startsWith('__'));
    const columnGroups = new Map<string, string[]>();

    filteredKeys.forEach((key) => {
      const header = getTableColumnHeader(key, primaryDimension);
      if (!columnGroups.has(header)) {
        columnGroups.set(header, []);
      }
      columnGroups.get(header)?.push(key);
    });

    // Return the first key for each unique header
    return Array.from(columnGroups.entries())
      .map(([_, keys]) => keys[0])
      .filter((key): key is string => Boolean(key));
  }, [allKeys, primaryDimension]);

  useEffect(() => {
    if (!isEqual(availableColumns, deduplicatedAvailableColumns)) {
      setAvailableColumns(deduplicatedAvailableColumns);
    }
  }, [deduplicatedAvailableColumns, availableColumns, setAvailableColumns]);

  // Deduplicate selected columns to match the deduplicated available columns
  const deduplicatedSelectedColumns = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) return [];

    const columnGroups = new Map<string, string[]>();

    selectedColumns
      .filter(
        (k) => !k.startsWith('__') && (filterColumn ? filterColumn(k) : true)
      )
      .forEach((key) => {
        const header = getTableColumnHeader(key, primaryDimension);
        if (!columnGroups.has(header)) {
          columnGroups.set(header, []);
        }
        columnGroups.get(header)?.push(key);
      });

    // Return the first key for each unique header that was selected
    return Array.from(columnGroups.entries())
      .map(([_, keys]) => keys[0])
      .filter((key): key is string => Boolean(key));
  }, [selectedColumns, primaryDimension, filterColumn]);

  // Update selected columns if they don't match the deduplicated version
  useEffect(() => {
    if (!isEqual(selectedColumns, deduplicatedSelectedColumns)) {
      setSelectedColumns(deduplicatedSelectedColumns);
    }
  }, [deduplicatedSelectedColumns, selectedColumns, setSelectedColumns]);

  useTableColumnSync(
    selectedColumns,
    setSelectedColumns,
    columnStorageKey,
    defaultColumns,
    deduplicatedAvailableColumns,
    { processResolvedColumns }
  );

  const columnDefs = useMemo(() => {
    const columnsToUse =
      deduplicatedSelectedColumns && deduplicatedSelectedColumns.length > 0
        ? deduplicatedSelectedColumns
        : deduplicatedAvailableColumns;

    if (!columnsToUse || columnsToUse.length === 0) {
      return undefined;
    }

    const defs: ColumnDef<TableRow, unknown>[] = columnsToUse.map((key) => {
      const header = getTableColumnHeader(key, primaryDimension);
      const isRestricted = isTrialUser && RESTRICTED_TRIAL_COLUMNS.has(key);

      const format = (() => {
        if (getRowDetail) {
          return getTalentDiscoveryFormat(header);
        }
        const columnMeta = columnMetaMap?.[key];
        if (columnMeta?.format !== undefined) {
          return columnMeta.format;
        }
        const metricForColumn = columnMeta?.metric;
        if (metricForColumn !== undefined) {
          return getFormat({
            metric: metricForColumn as MetricValues,
            displayAs: 'value',
          });
        }
        if (
          columnMetaMap === undefined &&
          (key === 'VALUE' || key === 'SHARE') &&
          item?.plot?.format !== undefined
        ) {
          return item.plot.format;
        }
        return FormatType.SI;
      })();

      const formatter = getFormatter(format, { currencyCode });

      const testIdKey = key
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const primaryColumnKey = getPrimaryColumnHeader(primaryDimension);
      const isPrimaryDimensionColumn =
        key === 'Primary' || key === primaryColumnKey;

      const isNonNumericDisplayColumn =
        key === 'linkedin_url' ||
        header === 'Profile URL' ||
        key === 'posting_url' ||
        header === 'Posting URL' ||
        key === 'Start Date' ||
        key === 'End Date' ||
        key.toLowerCase().includes('phone') ||
        header.toLowerCase().includes('phone');

      // When numericSortKeys is provided, restrict numeric sorting to only those keys.
      const isNumericColumn = (() => {
        if (numericSortKeys) {
          if (
            isNonNumericDisplayColumn ||
            isPrimaryDimensionColumn ||
            key === 'flight_risk' ||
            key === 'prestige' ||
            key === 'remote_suitability'
          ) {
            return false;
          }
          return numericSortKeys.has(key);
        }
        return (
          !isNonNumericDisplayColumn &&
          !isPrimaryDimensionColumn &&
          key !== 'flight_risk' &&
          key !== 'prestige' &&
          key !== 'remote_suitability'
        );
      })();

      return {
        id: key,
        accessorKey: key,
        header,
        meta: { isNumeric: isNumericColumn },
        cell: (info: CellContext<TableRow, unknown>) => {
          const value = info.getValue();
          const originalRow = info.row.original;
          const originalValues = originalRow?.__originalValues ?? {};
          const normalizedHeaderKey = migrateColumnNames([header])[0];
          const normalizedHeaderValue =
            normalizedHeaderKey != null
              ? originalValues[normalizedHeaderKey]
              : undefined;
          const originalValue =
            originalValues[key] ??
            normalizedHeaderValue ??
            originalValues[header];
          const rowUserKey = getRowUserKey(originalRow);

          const isProfileUrlColumn =
            key === 'linkedin_url' ||
            header === 'Profile URL' ||
            key === 'posting_url' ||
            header === 'Posting URL';

          // Special handling for URL columns (LinkedIn / posting URL)
          if (isProfileUrlColumn) {
            return (
              <ProfileUrlCell
                value={String(value ?? '')}
                isRestricted={isRestricted}
                encryptedValue={originalValue}
                onMouseEnter={showBlurToast}
                allowUnblur={allowUnblur}
                userKey={rowUserKey}
                fieldKey={key}
                data-testid={`table-cell-${testIdKey}`}
              />
            );
          }

          const isDateColumn = key === 'Start Date' || key === 'End Date';
          const isPhoneNumber =
            key.toLowerCase().includes('phone') ||
            header.toLowerCase().includes('phone');

          const formattedValue = (() => {
            if (isDateColumn) {
              return formatDateToMMMYYYY(value as string);
            }
            if (isPhoneNumber) {
              return formatPhoneNumber(value as string | number);
            }
            const numValue = typeof value === 'number' ? value : Number(value);
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            return !isNaN(numValue) && value != null && value !== ''
              ? formatter(numValue)
              : String(value ?? '');
          })();

          // Render cell (restricted or normal)
          if (isRestricted) {
            return (
              <Box data-testid={`table-cell-${testIdKey}`}>
                <BlurredCell
                  value={formattedValue}
                  encryptedValue={originalValue}
                  onMouseEnter={showBlurToast}
                  allowUnblur={allowUnblur}
                  userKey={rowUserKey}
                  fieldKey={key}
                />
              </Box>
            );
          }
          return (
            <Box
              data-testid={`table-cell-${testIdKey}`}
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {formattedValue}
            </Box>
          );
        },
        ...(key === 'flight_risk' ? { sortingFn: flightRiskSortingFn } : {}),
        ...(key === 'prestige' ? { sortingFn: prestigeSortingFn } : {}),
        ...(key === 'remote_suitability'
          ? { sortingFn: remoteSuitabilitySortingFn }
          : {}),
        ...(isNumericColumn ? { sortingFn: numericSortingFn } : {}),
      };
    });

    return defs;
  }, [
    deduplicatedSelectedColumns,
    deduplicatedAvailableColumns,
    item?.plot?.format,
    columnMetaMap,
    primaryDimension,
    isTrialUser,
    showBlurToast,
    allowUnblur,
    numericSortKeys,
    currencyCode,
    getRowDetail,
  ]);

  const { setSelectedRows, selectedRows } = useTableSelection();

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Index lookup built once per data change so syncing selection is O(selected)
  // instead of O(selected × rows) — data.indexOf() per selected row was
  // quadratic and made each toggle slower as the selection grew.
  const rowIndexByRef = useMemo(() => {
    const map = new Map<object, number>();
    (data ?? []).forEach((row, idx) => {
      map.set(row, idx);
    });
    return map;
  }, [data]);

  useEffect(() => {
    const next: Record<string, boolean> = {};
    (selectedRows ?? []).forEach((row) => {
      const idx = rowIndexByRef.get(row);
      if (idx !== undefined) next[String(idx)] = true;
    });
    // Bail if unchanged: the table already updated its own rowSelection on the
    // toggle, so re-deriving an equal object here would force a needless render.
    setRowSelection((prev) => {
      const nextKeys = Object.keys(next);
      if (
        nextKeys.length === Object.keys(prev).length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [selectedRows, rowIndexByRef]);

  const onRowSelectionChange = useCallback(
    (rows: object[]) => {
      setSelectedRows(rows as TableRow[]);
    },
    [setSelectedRows]
  );

  useEffect(() => {
    if (!data?.length) {
      setSelectedRows([]);
    }
  }, [data, setSelectedRows]);

  const exportRows = useMemo(() => {
    const columnsToUse =
      deduplicatedSelectedColumns && deduplicatedSelectedColumns.length > 0
        ? deduplicatedSelectedColumns
        : deduplicatedAvailableColumns;
    if (!columnsToUse || columnsToUse.length === 0) {
      return [];
    }
    return (data ?? []).map((row) => {
      const out: Record<string, unknown> = {};
      columnsToUse.forEach((key) => {
        if (key.startsWith('__')) return;
        const header = getTableColumnHeader(key, primaryDimension);
        out[header] = row[key];
      });
      return out;
    });
  }, [
    data,
    deduplicatedSelectedColumns,
    deduplicatedAvailableColumns,
    primaryDimension,
  ]);

  useEffect(() => {
    setCustomData(
      { type: DataType.TABLE_ROWS, data: exportRows },
      DataType.TABLE_ROWS
    );
  }, [exportRows, setCustomData]);

  const onRenderRow = useCallback(
    ({
      rowContent,
      rowData,
      gridRef,
      rowIndex,
    }: {
      rowContent: ReactNode;
      rowData: object;
      rowIndex: number;
      gridRef: HTMLDivElement | null;
    }) => {
      const rowDataRecord = rowData as Record<string, unknown>;

      const originalRowElement = rowContent as React.ReactElement;
      const originalClassName = originalRowElement?.props?.className || '';
      const originalStyle = originalRowElement?.props?.style || {};

      // Generate row key: use user_id for TD data, otherwise use rowIndex or primary dimension values
      const getRowKey = () => {
        if (rowDataRecord.user_id != null) {
          return String(rowDataRecord.user_id);
        }
        // For non-TD data, use rowIndex with primary dimension value if available
        const primaryKey =
          rowDataRecord.Primary ??
          rowDataRecord[getPrimaryColumnHeader(primaryDimension)] ??
          rowIndex;
        return `${primaryKey}-${rowIndex}`;
      };
      const rowKey = getRowKey();

      if (boundaryRef.current && gridRef && getRowDetail) {
        return (
          <RowPopout
            key={rowKey}
            gridRef={{
              getScrollLeft: () => gridRef.scrollLeft || 0,
              getDOMNode: () => gridRef,
            }}
            boundaryRef={boundaryRef}
            rowData={rowDataRecord}
            content={getRowDetail(rowDataRecord as TableRow)}
          >
            {(popoutProps) => {
              const { anchorRef, isHighlighted, ...popoutPropsWithoutRef } =
                popoutProps;

              return (
                <tr
                  className={classNames(
                    originalClassName,
                    isHighlighted && styles.rowHighlighted
                  )}
                  style={{
                    ...originalStyle,
                    cursor: 'pointer',
                  }}
                  key={`${rowKey}-${rowIndex}`}
                  {...popoutPropsWithoutRef}
                  ref={anchorRef}
                >
                  {originalRowElement?.props?.children}
                </tr>
              );
            }}
          </RowPopout>
        );
      }

      return (
        <tr
          className={originalClassName}
          style={{ ...originalStyle, cursor: 'pointer' }}
          key={`${rowKey}-${rowIndex}`}
        >
          {originalRowElement?.props?.children}
        </tr>
      );
    },
    [getRowDetail, primaryDimension]
  );

  if (!hasVisibleFilters) {
    return (
      <Flex
        h="100%"
        direction="column"
        justifyContent="center"
        alignItems="center"
        overflow="hidden"
      >
        <VStack>
          <Image as={DefaultIcon} alt="default placeholder icon" />
          <Text>Add a filter to view data.</Text>
        </VStack>
      </Flex>
    );
  }

  const hasUrlParam = window.location.search.includes('columns=');
  const shouldUseDefaults =
    defaultColumns != null && selectedColumns.length === 0 && !hasUrlParam;
  const columnModalDefaults = defaultColumns ?? [];

  const deduplicatedColumnOptions = availableColumns.map((column) => ({
    value: column,
    label: getTableColumnHeader(column, primaryDimension),
  }));

  return (
    <Box position="relative" h="100%" w="100%" ref={tableContainerRef}>
      {defaultColumns != null && data.length > 0 && (
        <Box position="absolute" right="0" top="0" zIndex={2}>
          <CustomiseColumnsButton
            onClick={onColumnModalOpen}
            right={0}
            variant="compact"
            height="38px"
            hideTopRightRadius
          />
        </Box>
      )}
      <TableChart<TableRow>
        data={data}
        searchTerm={searchTerm}
        columns={columnDefs}
        showZebraStripe={isReport || isNewTheme ? false : showZebraStripe}
        enableRowSelection={isReport ? false : enableRowSelection}
        onRowSelectionChange={
          enableRowSelection ? onRowSelectionChange : undefined
        }
        rowSelection={
          enableRowSelection && !isReport ? rowSelection : undefined
        }
        onRowSelectionStateChange={
          enableRowSelection && !isReport ? setRowSelection : undefined
        }
        onSortedDataChange={onSortedDataChange}
        onSortingStateNotify={onSortingStateNotify}
        boundaryRef={boundaryRef}
        onRenderRow={onRenderRow}
        className={classNames(styles.table, isReport && styles.reportTable)}
        autoFitColumns={isReport}
        onSelectRow={onSelectRow}
      />
      {defaultColumns != null && (
        <ColumnSelectModal
          isOpen={isColumnModalOpen}
          onClose={onColumnModalClose}
          columns={deduplicatedColumnOptions}
          savedCheckedColumns={selectedColumns}
          setSavedCheckedColumns={setSelectedColumns}
          initialCheckedColumn={
            shouldUseDefaults ? columnModalDefaults : undefined
          }
        />
      )}
    </Box>
  );
};
